import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { GetUsersParams, UserFilterValue } from '../types';

const ROLE_OPTIONS = ['ADMIN', 'USER'] as const;
const STATUS_OPTIONS = ['active', 'inactive'] as const;
const ANONYMOUS_OPTIONS = ['anonymous', 'registered'] as const;
const REGISTRATION_PROVIDER_OPTIONS = [
  'EMAIL',
  'GOOGLE',
  'FACEBOOK',
  'APPLE',
  'GITHUB',
  'ANONYMOUS',
  'ADMIN_CREATED',
  'UNKNOWN',
] as const;
const REGISTRATION_CLIENT_OPTIONS = ['WEB', 'IOS', 'ANDROID', 'ADMIN', 'API', 'UNKNOWN'] as const;

const FILTER_KEYS = [
  'search',
  'role',
  'status',
  'plan',
  'anonymous',
  'registrationProvider',
  'registrationClient',
] as const;

const emptyFilter: UserFilterValue = {
  search: '',
  role: '',
  status: '',
  plan: '',
  anonymous: '',
  registrationProvider: '',
  registrationClient: '',
};

const getValidParam = <T extends string>(
  searchParams: URLSearchParams,
  key: string,
  options: readonly T[]
): T | '' => {
  const value = searchParams.get(key);
  return options.includes(value as T) ? (value as T) : '';
};

const getInitialFilter = (searchParams: URLSearchParams): UserFilterValue => ({
  search: searchParams.get('search') || '',
  role: getValidParam(searchParams, 'role', ROLE_OPTIONS),
  status: getValidParam(searchParams, 'status', STATUS_OPTIONS),
  plan: searchParams.get('plan')?.trim().toUpperCase() || '',
  anonymous: getValidParam(searchParams, 'anonymous', ANONYMOUS_OPTIONS),
  registrationProvider: getValidParam(
    searchParams,
    'registrationProvider',
    REGISTRATION_PROVIDER_OPTIONS
  ),
  registrationClient: getValidParam(
    searchParams,
    'registrationClient',
    REGISTRATION_CLIENT_OPTIONS
  ),
});

const useDebounce = <T>(value: T, delay: number): T => {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);

  return debouncedValue;
};

interface UseUserFilterStateOptions {
  syncToUrl: boolean;
}

export const useUserFilterState = ({ syncToUrl }: UseUserFilterStateOptions) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filter, setFilterState] = useState<UserFilterValue>(() =>
    syncToUrl ? getInitialFilter(searchParams) : emptyFilter
  );
  const debouncedSearch = useDebounce(filter.search.trim(), 400);

  useEffect(() => {
    if (!syncToUrl) return;

    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        FILTER_KEYS.forEach((key) => next.delete(key));

        if (debouncedSearch) next.set('search', debouncedSearch);
        if (filter.role) next.set('role', filter.role);
        if (filter.status) next.set('status', filter.status);
        if (filter.plan) next.set('plan', filter.plan);
        if (filter.anonymous) next.set('anonymous', filter.anonymous);
        if (filter.registrationProvider) {
          next.set('registrationProvider', filter.registrationProvider);
        }
        if (filter.registrationClient) next.set('registrationClient', filter.registrationClient);

        return next;
      },
      { replace: true }
    );
  }, [
    debouncedSearch,
    filter.anonymous,
    filter.plan,
    filter.registrationClient,
    filter.registrationProvider,
    filter.role,
    filter.status,
    setSearchParams,
    syncToUrl,
  ]);

  const setFilter = (partial: Partial<UserFilterValue>) => {
    setFilterState((current) => ({
      ...current,
      ...partial,
      plan: partial.plan === undefined ? current.plan : partial.plan.trim().toUpperCase(),
    }));
  };

  const reset = () => setFilterState(emptyFilter);

  const apiParams = useMemo<GetUsersParams>(
    () => ({
      search: debouncedSearch || undefined,
      role: filter.role || undefined,
      status: filter.status || undefined,
      plan: filter.plan || undefined,
      anonymous: filter.anonymous || undefined,
      registrationProvider: filter.registrationProvider || undefined,
      registrationClient: filter.registrationClient || undefined,
    }),
    [
      debouncedSearch,
      filter.anonymous,
      filter.plan,
      filter.registrationClient,
      filter.registrationProvider,
      filter.role,
      filter.status,
    ]
  );

  return { filter, setFilter, reset, apiParams };
};
